import { userName } from './people';

// What a district manager (admin_level 'd') may open in the admin website. The server refuses the same
// (laravel App\CompanyScope::districtManagerRefusal): adding, editing or deleting district managers and
// company accounts, and the company account itself (the Account page and Billing). They work only inside
// their own district (laravel App\DistrictScope): districts are the company admin's.

// Their company's dashboard, as a company admin's (the sidebar's Dashboard)
export const DISTRICT_MANAGER_HOME = '/company-dashboard';

// Pages a district manager is sent back to their dashboard from (typed or bookmarked URLs)
export const DISTRICT_MANAGER_BLOCKED = [
	'/account',
	'/billing',
	'/district-manager-edit/:id',
	'/company-edit',
	'/reseller-edit/:id',
	// Not in their menu: the company list (other companies, the company account) and the support page
	'/companies',
	'/support-contact',
	// Districts are the company admin's
	'/district',
	'/district-edit/:id',
	'/district-info/:id',
	// Alert templates are shared by every company: the super admin's (SUPER_ADMIN_ONLY)
	'/alert-template',
];

// Pages only the super admin (admin_level 'a') opens; the server refuses everyone else's changes there
// (laravel AlertTemplateController). Others are sent back to their own home page.
export const SUPER_ADMIN_ONLY = ['/alert-template'];

// What the server answers a district manager who has no district yet (laravel App\DistrictScope::NO_DISTRICT)
export const NO_DISTRICT_MESSAGE = 'You have not been assigned a district yet. Please ask your company admin.';

function readLevel() {
	try {
		return localStorage.getItem('admin_level');
	} catch (e) {
		return null;
	}
}

export function isDistrictManager(level = readLevel()) {
	return level === 'd';
}

function readUser() {
	try {
		return JSON.parse(localStorage.getItem('mo_user'));
	} catch (e) {
		return null;
	}
}

function hasDistrict(user) {
	return !!user && Number(user.district_id) > 0;
}

// A district manager the company has not given a district yet (the sign-in says: district_id null)
export function districtManagerHasNoDistrict(level = readLevel(), user = readUser()) {
	return isDistrictManager(level) && !hasDistrict(user);
}

// For a district manager, the District and District Manager pickers of the supervisor, client and guard
// forms: their own district and themselves (the server saves that whatever is sent); null for others
export function ownDistrictPicks(level = readLevel(), user = readUser()) {
	if (!isDistrictManager(level) || !hasDistrict(user)) {
		return null;
	}
	return {
		districtOption: { value: String(user.district_id), label: user.district_name || '' },
		managerOption: { value: user.id, label: userName(user) },
	};
}

// True when a district manager may not open this path
export function blockedForDistrictManager(pathname, level = readLevel()) {
	if (!isDistrictManager(level) || typeof pathname !== 'string') {
		return false;
	}
	return matchesAny(pathname, DISTRICT_MANAGER_BLOCKED);
}

function matchesAny(pathname, paths) {
	const path = pathname.replace(/\/+$/, '') || '/';
	return paths.some((blocked) => {
		const base = blocked.replace(/\/:id$/, '');
		return path === base || path.startsWith(base + '/');
	});
}

export function isSuperAdmin(level = readLevel()) {
	return level === 'a';
}

// Where a signed-in admin who may not open this path is sent instead, or null when they may open it (or are
// not signed in: the sign-in redirect handles that)
export function blockedRedirect(pathname, level = readLevel()) {
	if (typeof pathname !== 'string') {
		return null;
	}
	if (blockedForDistrictManager(pathname, level)) {
		return DISTRICT_MANAGER_HOME;
	}
	if ((level === 'b' || level === 'c') && matchesAny(pathname, SUPER_ADMIN_ONLY)) {
		return level === 'c' ? '/company-dashboard' : '/';
	}
	return null;
}

// Only a company admin, reseller or super-admin adds, edits or deletes district managers
export function canManageDistrictManagers(level = readLevel()) {
	return !isDistrictManager(level);
}
